import type { CSSProperties, ReactNode } from 'react';
import { cx } from './tokens';

export type PanelTone = 'raised' | 'sunk' | 'ink' | 'red' | 'teal';

const TONES: Record<PanelTone, string> = {
  raised: 'bg-paper-raised text-ink',
  sunk: 'bg-paper-sunk text-ink',
  ink: 'bg-ink text-paper',
  red: 'bg-red text-paper',
  teal: 'bg-teal text-paper',
};

const PRESS = { none: null, sm: '2px', md: '4px', lg: '7px' } as const;

/**
 * A panel of printed stock: a hard rectangle with a thick rule and an offset
 * ink slab behind it. The slab is a layer rather than a box-shadow so the face
 * can carry its own inset rules.
 */
export function Panel({
  children,
  className,
  innerClassName,
  tone = 'raised',
  press = 'md',
  rule = false,
  style,
}: {
  children: ReactNode;
  className?: string;
  innerClassName?: string;
  tone?: PanelTone;
  press?: keyof typeof PRESS;
  /** Inset deco hairline, for surfaces that want to read as a printed plate. */
  rule?: boolean;
  style?: CSSProperties;
}) {
  const offset = PRESS[press];

  /*
   * Three layers on purpose. The shadow box (`.pressed`) must always hug the
   * face exactly, because its `::before` is positioned `inset: 0` against it —
   * so any padding a caller passes has to land on an outer element, or the
   * shadow inflates into a slab the size of the padding box.
   */
  return (
    <div className={cx('relative', className)} style={style}>
      <div
        className={cx('h-full w-full', offset && 'pressed')}
        style={offset ? ({ '--press': offset } as CSSProperties) : undefined}
      >
        <div
          className={cx(
            'press-face relative h-full border-[3px] border-ink',
            rule && 'inner-rule',
            TONES[tone],
            innerClassName,
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
